import React, { useState } from "react";
import { toast } from "sonner";
import { Handshake, Save } from "lucide-react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

/**
 * Modal to activate / configure the Faz Depois Paga for a school
 * without leaving the current page. On save, calls onSaved(updatedSchool).
 */
export default function FdpActivateDialog({ open, onOpenChange, school, onSaved }) {
  const [form, setForm] = useState({
    fdp_active: true,
    fdp_max_value: 0,
    fdp_period_months: 6,
    fdp_start_date: new Date().toISOString().slice(0, 10),
  });
  const [saving, setSaving] = useState(false);

  // Prefill from school whenever opened
  React.useEffect(() => {
    if (!school) return;
    setForm({
      fdp_active: true,
      fdp_max_value: school.fdp_max_value || 0,
      fdp_period_months: school.fdp_period_months || 6,
      fdp_start_date: school.fdp_start_date || new Date().toISOString().slice(0, 10),
    });
  }, [school, open]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!school) return;
    if (!form.fdp_max_value || form.fdp_max_value <= 0) {
      toast.error("Informe o valor máximo (limite) da modalidade");
      return;
    }
    if (!form.fdp_period_months || form.fdp_period_months <= 0) {
      toast.error("Informe o período em meses");
      return;
    }
    setSaving(true);
    try {
      const r = await api.patch(`/fdp/schools/${school.id}`, form);
      toast.success("Faz Depois Paga ativado para esta escola");
      onSaved?.(r.data.school);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao ativar FDP");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="fdp-activate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1e3a8a]">
            <Handshake className="w-5 h-5 text-violet-600" />
            Ativar Faz Depois Paga
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <p className="text-xs text-slate-500">
            Configure os limites para a escola{" "}
            <b className="text-slate-800">{school?.name || "—"}</b> poder usar a modalidade
            Faz Depois Paga. Esta ação não interrompe a criação do orçamento.
          </p>

          <div className="flex items-center gap-3 p-3 rounded-lg bg-violet-50 border border-violet-200">
            <Switch
              checked={form.fdp_active}
              onCheckedChange={(v) => update("fdp_active", !!v)}
              data-testid="fdp-switch-active"
            />
            <div>
              <p className="font-semibold text-slate-900 text-sm">Ativar modalidade FDP</p>
              <p className="text-xs text-slate-600">
                Habilita a escola a receber orçamentos no sistema Faz Depois Paga.
              </p>
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Valor máximo (R$)
            </Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={form.fdp_max_value}
              onChange={(e) => update("fdp_max_value", Number(e.target.value) || 0)}
              placeholder="Ex.: 5000,00"
              data-testid="fdp-input-max-value"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Período (meses)
              </Label>
              <Input
                type="number"
                min="1"
                value={form.fdp_period_months}
                onChange={(e) => update("fdp_period_months", Number(e.target.value) || 0)}
                data-testid="fdp-input-period"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Início
              </Label>
              <Input
                type="date"
                value={form.fdp_start_date}
                onChange={(e) => update("fdp_start_date", e.target.value)}
                data-testid="fdp-input-start"
              />
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-[#7c3aed] hover:bg-[#6d28d9] text-white"
            data-testid="fdp-save-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Salvando..." : "Salvar e ativar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
